import React, { useEffect, useState } from "react";
import http from "../../../../services/http";

/**
 * 01/11/2026 (Quân): hồ sơ ký của trạm — đủ đèn mới mời ký; duyệt chữ ký có họ tên lệch người được ký.
 * Backend: GET /admin/partners/:id/signing-file · POST signing-request · POST signature-review.
 */
type Item = { key: string; ok: boolean; detail: string };
type Review = { status: string; reason?: string | null; matchedAs?: string | null; expected?: string[] };
type Cert = {
	code: string;
	signerName: string;
	signerCapacity: string;
	emailStatus: string;
	createdAt: string;
	review: Review | null;
	/** Chỉ có khi ký trước bước duyệt (không có review): so họ tên với người được ký hiện tại. */
	nameCheck?: "match" | "mismatch" | "business" | null;
};
type HoSo = {
	ready: boolean;
	error?: boolean;
	items: Item[];
	expectedSigners: Array<{ name: string; as: string }>;
	signRequest: { requestedAt?: string; reason?: string | null; expectedSignerName?: string } | null;
	certificates: Cert[];
};

const NHAN: Record<string, string> = {
	licence: "Business licence",
	bank: "Payout account",
	payee: "Account holder",
	chain: "Chain",
	signer: "Who may sign",
};
const TRANG_THAI: Record<string, { label: string; fg: string; bg: string }> = {
	pending: { label: "Waiting for review", fg: "#B54708", bg: "#FFFAEB" },
	auto_approved: { label: "Matched — sent", fg: "#067647", bg: "#ECFDF3" },
	approved: { label: "Approved — sent", fg: "#067647", bg: "#ECFDF3" },
	rejected: { label: "Rejected", fg: "#B42318", bg: "#FEF3F2" },
	void: { label: "Void", fg: "#B42318", bg: "#FEF3F2" },
	// Ký trước khi có bước duyệt — chỉ so tên để Admin thấy chỗ lệch.
	legacy: { label: "Signed before review", fg: "#475467", bg: "#F2F4F7" },
	legacy_match: { label: "Signed before review — name matches", fg: "#067647", bg: "#ECFDF3" },
	legacy_mismatch: { label: "Signed before review — name differs", fg: "#B54708", bg: "#FFFAEB" },
	legacy_business: { label: "Signed before review — business name", fg: "#B42318", bg: "#FEF3F2" },
};
const nut = (chinh = false): React.CSSProperties => ({
	border: chinh ? "none" : "1px solid #D0D5DD",
	background: chinh ? "#0F5F9E" : "#fff",
	color: chinh ? "#fff" : "#344054",
	borderRadius: 8,
	padding: "5px 12px",
	cursor: "pointer",
	fontSize: 12.5,
	fontWeight: 600,
});
const loiCua = (e: any) => e?.response?.data?.message || "Something went wrong.";

export default function HoSoKyTram(props: { partnerId: string; onChanged?: () => void }) {
	const [hs, setHs] = useState<HoSo | null>(null);
	const [busy, setBusy] = useState("");
	const [tb, setTb] = useState("");
	const [moYc, setMoYc] = useState(false);
	const [tenUq, setTenUq] = useState("");
	const [ghiChu, setGhiChu] = useState("");

	const tai = () =>
		http
			.get(`/admin/partners/${props.partnerId}/signing-file`)
			.then(({ data }) => setHs(data?.data || null))
			.catch(() => setHs(null));
	useEffect(() => {
		tai();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.partnerId]);

	const yeuCau = async () => {
		setBusy("request");
		setTb("");
		try {
			await http.post(`/admin/partners/${props.partnerId}/signing-request`, {
				expectedSignerName: tenUq.trim() || undefined,
				note: ghiChu.trim() || undefined,
			});
			setMoYc(false);
			setTenUq("");
			setGhiChu("");
			setTb("Signature requested — the station sees the signing screen next time it opens the portal.");
			await tai();
			props.onChanged?.();
		} catch (e) {
			setTb(loiCua(e));
		} finally {
			setBusy("");
		}
	};

	const duyet = async (c: Cert, action: "approve" | "reject" | "void") => {
		let reason = "";
		if (action !== "approve") {
			reason =
				window.prompt(
					action === "void"
						? `Void certificate ${c.code}? The station must sign again. Reason (shown to the station):`
						: `Reject the signature "${c.signerName}"? Reason (shown to the station):`
				) || "";
			if (!reason.trim()) return;
		}
		setBusy(c.code);
		setTb("");
		try {
			await http.post(`/admin/partners/${props.partnerId}/signature-review`, { certCode: c.code, action, reason });
			setTb(action === "approve" ? `${c.code} approved — certificate emailed.` : `${c.code} ${action === "void" ? "voided" : "rejected"} — the station is asked to sign again.`);
			await tai();
			props.onChanged?.();
		} catch (e) {
			setTb(loiCua(e));
		} finally {
			setBusy("");
		}
	};

	if (!hs || hs.error) return null;
	const daYc = hs.signRequest?.requestedAt;
	return (
		<div data-testid="signing-file" style={{ marginTop: 12, padding: 10, border: "1px solid #EAECF0", borderRadius: 8, fontSize: 12.5, color: "#344054" }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
				<strong>Signing file</strong>
				<span style={{ fontWeight: 600, color: hs.ready ? "#067647" : "#B54708" }}>
					{hs.ready ? "Complete — ready to sign" : "Not complete — the station is not asked to sign yet"}
				</span>
			</div>
			<div style={{ display: "grid", gap: 4, marginTop: 6 }}>
				{hs.items.map((i) => (
					<div key={i.key} data-testid={`signing-item-${i.key}`} style={{ display: "flex", gap: 8 }}>
						<span style={{ width: 14, color: i.ok ? "#067647" : "#B42318", fontWeight: 700 }}>{i.ok ? "✓" : "✗"}</span>
						<span style={{ width: 110, color: "#667085" }}>{NHAN[i.key] || i.key}</span>
						<span style={{ flex: 1 }}>{i.detail}</span>
					</div>
				))}
			</div>
			<div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
				{daYc ? (
					<span style={{ color: "#667085" }}>
						Signature requested {new Date(daYc).toLocaleString()}
						{hs.signRequest?.expectedSignerName ? ` · authorised signer: ${hs.signRequest.expectedSignerName}` : ""}
					</span>
				) : null}
				{!moYc ? (
					<button type="button" data-testid="request-signature" disabled={!hs.ready} title={hs.ready ? "" : "Complete the signing file first"} onClick={() => setMoYc(true)} style={nut(true)}>
						{daYc ? "Request signature again" : "Request signature"}
					</button>
				) : null}
			</div>
			{moYc ? (
				<div style={{ display: "grid", gap: 6, marginTop: 6, maxWidth: 420 }}>
					<span style={{ color: "#667085" }}>
						Leave empty: the representative on the licence signs. If someone else signs (e.g. a manager), write their full
						name — they confirm their authority when signing; no authority letter is needed (decided 28/10).
					</span>
					<input placeholder="Authorised signer's full name (optional)" value={tenUq} onChange={(e) => setTenUq(e.target.value)} style={{ padding: "6px 8px", border: "1px solid #D0D5DD", borderRadius: 8 }} />
					<input placeholder="Internal note (the station does not see it)" value={ghiChu} onChange={(e) => setGhiChu(e.target.value)} style={{ padding: "6px 8px", border: "1px solid #D0D5DD", borderRadius: 8 }} />
					<span style={{ display: "flex", gap: 6 }}>
						<button type="button" disabled={busy === "request"} onClick={yeuCau} style={nut(true)}>
							{busy === "request" ? "Requesting…" : "Request"}
						</button>
						<button type="button" onClick={() => setMoYc(false)} style={nut()}>
							Cancel
						</button>
					</span>
				</div>
			) : null}
			{hs.certificates.length ? (
				<div style={{ marginTop: 10 }}>
					<strong>Signatures</strong>
					{hs.certificates.map((c) => {
						const st = c.review?.status || (c.nameCheck ? `legacy_${c.nameCheck}` : "legacy");
						const t = TRANG_THAI[st] || TRANG_THAI.pending;
						return (
							<div key={c.code} data-testid={`signature-${c.code}`} style={{ borderTop: "1px solid #EAECF0", padding: "6px 0", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
								<span style={{ flex: "1 1 260px" }}>
									<strong>{c.code}</strong> · {new Date(c.createdAt).toLocaleString()} · signed as <strong>{c.signerName}</strong>
									{c.review?.expected?.length ? <span style={{ color: "#667085" }}> · expected {c.review.expected.join(" / ")}</span> : null}
									{c.review?.reason ? <div style={{ color: "#B42318" }}>Reason: {c.review.reason}</div> : null}
								</span>
								<span style={{ padding: "2px 8px", borderRadius: 10, background: t.bg, color: t.fg, fontWeight: 600 }}>{t.label}</span>
								{st === "pending" ? (
									<>
										<button type="button" disabled={busy === c.code} onClick={() => duyet(c, "approve")} style={nut(true)}>
											Approve
										</button>
										<button type="button" disabled={busy === c.code} onClick={() => duyet(c, "reject")} style={nut()}>
											Reject
										</button>
									</>
								) : st !== "rejected" && st !== "void" ? (
									<button type="button" disabled={busy === c.code} onClick={() => duyet(c, "void")} style={nut()}>
										Void
									</button>
								) : null}
							</div>
						);
					})}
				</div>
			) : null}
			{tb ? <div style={{ marginTop: 6, color: "#344054" }}>{tb}</div> : null}
		</div>
	);
}
